"use client";

import { useEffect, useRef } from 'react';
import { auth } from '../config/firebaseConfig';
import useUserStore from '@/store/userStore';
import useCartStore from '@/store/cartStore';
import { syncTokenWithCookie } from '../utils/tokenManager';

// Global flag to prevent multiple initializations across page navigations
let hasInitialized = false;

/**
 * Component that initializes Firebase authentication
 * and synchronizes it with application state
 */
const AuthInitializer = () => {
  const {
    subscribeAuthChanges,
    resetLoadingState,
    loading,
    isLoggedIn,
    user,
    fetchUserProfile
  } = useUserStore();

  const {
    initAuthListener,
    fetchCartContents
  } = useCartStore();

  // Use a ref to track component mount/unmount
  const isMounted = useRef(false);

  // Effect for handling token refresh
  useEffect(() => {
    const refreshAndSyncToken = async () => {
      if (auth.currentUser) {
        try {
          const token = await auth.currentUser.getIdToken(true);
          await syncTokenWithCookie(token);
        } catch (error) {
          console.error('Error refreshing token:', error);
        }
      }
    };

    // Refresh token immediately and set up periodic refresh
    refreshAndSyncToken();

    // Refresh token every 10 minutes
    const intervalId = setInterval(refreshAndSyncToken, 10 * 60 * 1000);

    // Refresh token when tab becomes visible
    const visibilityHandler = () => {
      if (document.visibilityState === 'visible' && auth.currentUser) {
        refreshAndSyncToken();
      }
    };

    document.addEventListener('visibilitychange', visibilityHandler);

    return () => {
      clearInterval(intervalId);
      document.removeEventListener('visibilitychange', visibilityHandler);
    };
  }, []);

  // Effect for handling auth initialization
  useEffect(() => {
    isMounted.current = true;

    // Prevent backend verification on navigation, but ALLOW profile fetching
    if (typeof window !== 'undefined' && isLoggedIn && user) {
      try {
        sessionStorage.setItem('backend_verified', 'true');
        useUserStore.setState({ verifiedWithBackend: true });
        window.verificationInProgress = false;
        window.lastVerificationTime = Date.now();
      } catch (e) {
        console.error('Failed to update verification status:', e);
      }
    }

    // Skip initialization if already done
    if (hasInitialized) {
      if (isLoggedIn && user && !user.photoURL && fetchUserProfile) {
        fetchUserProfile();
      }
      return () => {};
    }

    try {
      // Mark as initialized globally
      hasInitialized = true;

      let unsubscribe = () => {};
      if (subscribeAuthChanges) {
        unsubscribe = subscribeAuthChanges();
      }

      if (initAuthListener) {
        initAuthListener();
      }

      // Initial cart fetch (with a small delay)
      let cartFetchTimeout = setTimeout(() => {
        if (isMounted.current && fetchCartContents) {
          fetchCartContents(true).catch(err => console.error('Error in initial cart fetch:', err));
        }
      }, 1000);

      // Safety timeout if auth loading gets stuck
      const safetyTimeout = setTimeout(() => {
        if (loading && resetLoadingState) {
          resetLoadingState();
        }

        // Also reset any cart operation flags if they get stuck
        useCartStore.setState({
          fetchInProgress: false,
          addInProgress: false,
          removeInProgress: false,
          clearInProgress: false
        });
      }, 10000);

      return () => {
        isMounted.current = false;
        clearTimeout(cartFetchTimeout);
        clearTimeout(safetyTimeout);

        if (typeof unsubscribe === 'function') {
          unsubscribe();
        }
      };
    } catch (error) {
      console.error('Error in AuthInitializer:', error);
      hasInitialized = false;
      return () => {};
    }
  }, [
    fetchCartContents,
    fetchUserProfile,
    initAuthListener,
    isLoggedIn,
    loading,
    resetLoadingState,
    subscribeAuthChanges,
    user
  ]);

  // This component doesn't render anything
  return null;
};

export default AuthInitializer;
